/**
 * The published reports' component set, ported.
 *
 * Every function here is a direct port of a renderer in the live report pages
 * (ops/report-nightly/pages/*.html in qcomm-blinkit-warehouse) — `fmt`,
 * `sparkline`, `deltaHTML`, `kpiCard`, `metricCell`, `tableCard`,
 * `findingsCard`. The arithmetic and the markup are copied, not reinterpreted,
 * because the in-app version had drifted into a different-looking product:
 * plain tables where the report shows sparklines, coloured deltas and progress
 * bars.
 *
 * Pair with ./ds.css, which carries the real CSS. Wrap a page in
 * <div className="rds"> for either to apply.
 */
import type { ReactNode } from "react";

/* ── formatting ───────────────────────────────────────────────────────────
 * Port of fmt(v,k). Indian grouping, Cr/L money, em-dash for anything that
 * is not a number — a blank cell and a zero must never look the same.
 */
export type FmtKind = "money" | "int" | "pct" | "pct2" | "ratio" | "price";

export function fmt(v: unknown, k: FmtKind): string {
  const n = typeof v === "number" ? v : Number(v);
  if (v === null || v === undefined || !Number.isFinite(n)) return "—";
  switch (k) {
    case "money":
      return n >= 1e7
        ? `₹${(n / 1e7).toFixed(2)} Cr`
        : n >= 1e5
          ? `₹${(n / 1e5).toFixed(2)} L`
          : `₹${Math.round(n).toLocaleString("en-IN")}`;
    case "int":
      return Math.round(n).toLocaleString("en-IN");
    case "pct":
      return `${n.toFixed(1)}%`;
    case "pct2":
      return `${n.toFixed(2)}%`;
    case "ratio":
      return `${n.toFixed(2)}x`;
    case "price":
      return `₹${n.toFixed(2)}`;
    default:
      return String(n);
  }
}

/* ── delta ────────────────────────────────────────────────────────────────
 * Port of deltaHTML(c,p,good). `good` says which direction is good, so a
 * falling ACOS is green and a falling share is red.
 *
 * "no comparison" is rendered rather than a 0% — the report never fakes a
 * delta it cannot compute, and a fabricated 0% is indistinguishable from a
 * real one.
 */
export function Delta({
  cur,
  prev,
  good = "up",
  pp = false,
}: {
  cur: number | null | undefined;
  prev: number | null | undefined;
  /** 'up' = higher is better, 'down' = lower is better, 'neutral' = never coloured. */
  good?: "up" | "down" | "neutral";
  /** Percentage-point change instead of percent change — for shares and rates. */
  pp?: boolean;
}) {
  const c = typeof cur === "number" && Number.isFinite(cur) ? cur : null;
  const p = typeof prev === "number" && Number.isFinite(prev) ? prev : null;
  if (c === null || p === null || (!pp && !p)) {
    return <div className="delta flat">no comparison</div>;
  }
  const raw = pp ? c - p : ((c - p) / Math.abs(p)) * 100;
  const decimals = pp ? 2 : 1;
  // Round first so -0.003 becomes 0.0, not -0.0
  const ch = Math.round(raw * 10 ** decimals) / 10 ** decimals;
  const unit = pp ? " pp" : "%";
  /**
   * Two flat bands, because the report has two.
   *
   * `ppHTML` (shares, OSA, listing, discount) calls anything under 0.02 pp
   * flat; `deltaHTML` (money, units, stores) uses 0.05%. Using one threshold
   * for both made a 0.03 pp share move render grey while the sparkline beside
   * it — which follows the 0.02 rule — rendered green. Both halves of the
   * colour rule have to sit on the same threshold or the tile contradicts
   * itself.
   */
  if (Math.abs(ch) < (pp ? 0.02 : 0.05) || good === "neutral") {
    return (
      <div className="delta flat">
        {ch.toFixed(decimals)}
        {unit}
      </div>
    );
  }
  const tone = (good === "down" ? ch < 0 : ch > 0) ? "up" : "down";
  return (
    <div className={`delta ${tone}`}>
      {ch > 0 ? "+" : ""}
      {ch.toFixed(decimals)}
      {unit}
    </div>
  );
}

/* ── sparkline ────────────────────────────────────────────────────────────
 * Port of sparkline(vals,tone,w,h): filled area at 8% plus a 1.5px polyline.
 * Fewer than two real points draws an empty svg rather than a misleading
 * flat line.
 */
export function Sparkline({
  values,
  tone = "flat",
  w = 84,
  h = 18,
}: {
  values: Array<number | null | undefined>;
  tone?: "up" | "down" | "flat";
  w?: number;
  h?: number;
}) {
  const c = values.filter(
    (v): v is number => typeof v === "number" && Number.isFinite(v),
  );
  if (c.length < 2) return <svg className="spark" width={w} height={h} />;
  const mn = Math.min(...c);
  const mx = Math.max(...c);
  const sp = mx - mn || 1;
  const xy = c.map(
    (v, i) =>
      [
        (i / (c.length - 1)) * (w - 2) + 1,
        h - 2 - ((v - mn) / sp) * (h - 4),
      ] as const,
  );
  const line = xy.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const col =
    tone === "up"
      ? "var(--up)"
      : tone === "down"
        ? "var(--down)"
        : "currentColor";
  const first = xy[0];
  const last = xy[xy.length - 1];
  if (!first || !last) return <svg className="spark" width={w} height={h} />;
  return (
    <svg
      className="spark"
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
    >
      <polygon
        points={`${first[0].toFixed(1)},${h} ${line} ${last[0].toFixed(1)},${h}`}
        fill={col}
        fillOpacity=".08"
      />
      <polyline
        points={line}
        fill="none"
        stroke={col}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── KPI ──────────────────────────────────────────────────────────────────
 * Port of kpiCard. The foot line always renders: it carries the basis, and a
 * figure whose basis is not stated is the defect this whole rebuild exists
 * to remove.
 */
export function Kpis({ children }: { children: ReactNode }) {
  return <div className="kpis">{children}</div>;
}

export function Kpi({
  label,
  value,
  delta,
  spark,
  foot,
  title,
}: {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  spark?: ReactNode;
  foot?: ReactNode;
  title?: string;
}) {
  return (
    <div className="kpi" {...(title ? { title } : {})}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-figs">
        <div className="kpi-val">{value}</div>
        {delta}
      </div>
      {spark ? <div className="kpi-spark">{spark}</div> : null}
      <div className="kpi-foot">{foot}</div>
    </div>
  );
}

/* ── table cell helpers ───────────────────────────────────────────────────
 * Port of metricCell: value, then optional delta, then optional spark,
 * stacked right-aligned.
 */
export function MetricCell({
  value,
  delta,
  spark,
  align,
}: {
  value: ReactNode;
  delta?: ReactNode;
  spark?: ReactNode;
  align?: "l" | "center";
}) {
  return (
    <div className={`metric${align ? ` ${align}` : ""}`}>
      <div className="metric-figs">
        <div className="metric-val">{value}</div>
      </div>
      {delta}
      {spark}
    </div>
  );
}

/** The report's two-line row label: name over a muted sub-line. */
export function RowLabel({ name, sub }: { name: ReactNode; sub?: ReactNode }) {
  return (
    <div className="rowlabel">
      <div>
        <div className="rowlabel-name">{name}</div>
        {sub ? <div className="rowlabel-sub">{sub}</div> : null}
      </div>
    </div>
  );
}

/** Share-of-total bar inside a cell — track plus fill, value above. */
export function ProgressCell({
  value,
  pct,
  tone,
}: {
  value: ReactNode;
  pct: number | null;
  tone?: "up" | "down";
}) {
  const w =
    pct === null || !Number.isFinite(pct) ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className="cell-progress">
      <div className="metric-val">{value}</div>
      <div className="cell-progress-track">
        <div
          className="cell-progress-fill"
          style={{
            width: `${w}%`,
            ...(tone ? { background: `var(--${tone})` } : {}),
          }}
        />
      </div>
    </div>
  );
}

export function Pill({
  level,
  children,
}: {
  level: "high" | "medium" | "low";
  children: ReactNode;
}) {
  return <span className={`pill ${level}`}>{children}</span>;
}

/* ── cards ────────────────────────────────────────────────────────────────
 * Port of tableCard. First column left-aligned, the rest take their
 * alignment from the column's `type` on BOTH th and td, which is what keeps
 * a number column reading as a column.
 */
export interface Col {
  label: string;
  /** 'num' | 'center' — applied to th and td alike. */
  type?: "num" | "center";
  title?: string;
}

export function Card({
  title,
  scope,
  asof,
  fixedWindow,
  actions,
  foot,
  children,
}: {
  title: string;
  scope?: string;
  asof?: string;
  fixedWindow?: boolean;
  actions?: ReactNode;
  foot?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card">
      <div className="card-head">
        <span className="card-title">{title}</span>
        {scope ? <span className="scope-pill">{scope}</span> : null}
        {fixedWindow ? (
          <span className="fixed-window">fixed window</span>
        ) : null}
        <span className="spacer" />
        {actions}
        {asof ? <span className="asof">{asof}</span> : null}
      </div>
      {children}
      {foot ? <div className="footnote">{foot}</div> : null}
    </section>
  );
}

export function TableCard({
  title,
  scope,
  asof,
  fixedWindow,
  actions,
  foot,
  cols,
  rows,
}: {
  title: string;
  scope?: string;
  asof?: string;
  fixedWindow?: boolean;
  actions?: ReactNode;
  foot?: ReactNode;
  cols: Col[];
  rows: Array<{
    key: string;
    cls?: string;
    onClick?: () => void;
    cells: ReactNode[];
  }>;
}) {
  return (
    <Card
      title={title}
      {...(scope ? { scope } : {})}
      {...(asof ? { asof } : {})}
      {...(fixedWindow ? { fixedWindow } : {})}
      {...(actions ? { actions } : {})}
      {...(foot ? { foot } : {})}
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th
                  key={c.label}
                  {...(i === 0 ? {} : c.type ? { className: c.type } : {})}
                  {...(c.title ? { title: c.title } : {})}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                {...(r.cls ? { className: r.cls } : {})}
                {...(r.onClick
                  ? { onClick: r.onClick, style: { cursor: "pointer" } }
                  : {})}
              >
                {r.cells.map((cell, i) => (
                  <td
                    // Column position IS the identity here — a table's cells
                    // are ordered and never reordered, so the index is stable.
                    key={cols[i]?.label ?? i}
                    {...(i === 0
                      ? {}
                      : cols[i]?.type
                        ? { className: cols[i]?.type }
                        : {})}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ── findings ─────────────────────────────────────────────────────────────
 * Port of findingsCard. Each finding shows the figures behind it in
 * `meta`, so every claim on the page can be checked without leaving it.
 */
export function Findings({
  title,
  items,
  disclaimer,
}: {
  title: string;
  items: Array<{ key: string; text: ReactNode; meta: ReactNode }>;
  disclaimer?: ReactNode;
}) {
  return (
    <section className="card">
      <div className="card-head findings-head">
        <span className="card-title">{title}</span>
        <span className="findings-count">
          {items.length} finding{items.length === 1 ? "" : "s"}
        </span>
      </div>
      {items.length ? (
        items.map((f) => (
          <div className="finding" key={f.key}>
            <div className="finding-text">{f.text}</div>
            <div className="finding-meta">{f.meta}</div>
          </div>
        ))
      ) : (
        <div className="empty">
          Nothing crosses a threshold for this period.
        </div>
      )}
      {disclaimer ? <div className="footnote">{disclaimer}</div> : null}
    </section>
  );
}

/** The page's basis banner — the "one basis, stated everywhere" strip. */
export function Banner({ children }: { children: ReactNode }) {
  // Suppressed on every page, on request: the yellow notice strip sat above the
  // first card on all six pages and pushed the numbers below the fold.
  //
  // Hidden HERE rather than at the ~20 render sites so the disclosures the strips
  // carry — the basis each rupee is on, the day the page runs to, a comparison
  // period that does not exist — stay written down beside the figures they
  // qualify, and stay maintained with them. Deleting the call sites would have
  // deleted the text too, and it is the only place the page says which basis it
  // is on. One line restores them.
  //
  // The red RECONCILIATION FAILED strip in CeoPages does NOT come through here:
  // it is an alarm, not a notice, and it must never be silenced by a display
  // preference.
  void children;
  return null;
}
